import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import LanguageSelect from '../shared/LanguageSelect';
import axios from 'axios';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';

// Helper to convert "yyyy-MM-dd" string to Date object
const dateStringToDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr + 'T00:00:00');
};

// Helper to convert Date object to "yyyy-MM-dd" string
const dateToDateString = (date) => {
  if (!date || isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function EditInvoiceHeaderDialog({ open, handleClose, invoice, onUpdate, focusField, setSnackbar }) {
  const { t } = useTranslation();
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(null);
  const [dueDate, setDueDate] = useState(null);
  const [notes, setNotes] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddressStreet, setCustomerAddressStreet] = useState('');
  const [customerAddressPostalCode, setCustomerAddressPostalCode] = useState('');
  const [customerAddressCity, setCustomerAddressCity] = useState('');
  const [buyersReference, setBuyersReference] = useState('');
  const [footerText, setFooterText] = useState('');
  const [language, setLanguage] = useState('fi');
  const [loading, setLoading] = useState(false);
  const notesRef = useRef(null);

  useEffect(() => {
    if (invoice) {
      setInvoiceNumber(invoice.invoice_number || '');
      setInvoiceDate(dateStringToDate(invoice.invoice_date));
      setDueDate(dateStringToDate(invoice.due_date));
      setNotes(invoice.notes || '');
      setCustomerEmail(invoice.customer_email || '');
      setCustomerAddressStreet(invoice.customer_address_street || '');
      setCustomerAddressPostalCode(invoice.customer_address_postal_code || '');
      setCustomerAddressCity(invoice.customer_address_city || '');
      setBuyersReference(invoice.buyers_reference || '');
      setFooterText(invoice.footer_text || '');
      setLanguage(invoice.language || 'fi');
    }
  }, [invoice]);

  // Focus the specified field after dialog animation completes
  const handleDialogEntered = () => {
    if (focusField === 'notes' && notesRef.current) {
      const input = notesRef.current;
      input.focus();
      // Move cursor to end of text
      const length = input.value?.length || 0;
      input.setSelectionRange(length, length);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const data = {
        invoice_number: invoiceNumber,
        invoice_date: dateToDateString(invoiceDate),
        buyers_reference: buyersReference,
        due_date: dateToDateString(dueDate),
        notes,
        customer_email: customerEmail,
        customer_address_street: customerAddressStreet,
        customer_address_postal_code: customerAddressPostalCode,
        customer_address_city: customerAddressCity,
        footer_text: footerText,
        language,
      };
      await axios.patch(`/api/invoices/${invoice.id}/`, data);
      // Fetch updated invoice
      const invoiceResponse = await axios.get(`/api/invoices/${invoice.id}/`);
      onUpdate(invoiceResponse.data);
      handleClose();
    } catch (err) {
      const message = t(err.response?.data?.error || 'Error updating invoice');
      if (setSnackbar) {
        setSnackbar({ open: true, message, severity: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{
        onEntered: handleDialogEntered,
      }}
      PaperProps={{
        sx: { borderRadius: '16px' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 600,
            fontSize: '18px',
          }}
        >
          {t('Edit invoice details')}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Invoice Number */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {t('Invoice number')}
          </Typography>
          <TextField
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            size="small"
            sx={{
              width: '200px',
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
            }}
          />
        </Box>

        {/* Invoice Date & Due Date */}
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
          <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
                {t('Invoice date')}
              </Typography>
              <DatePicker
                value={invoiceDate}
                onChange={(newValue) => setInvoiceDate(newValue)}
                format="dd.MM.yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: '180px',
                      '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                    },
                  },
                }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
                {t('Due date')}
              </Typography>
              <DatePicker
                value={dueDate}
                onChange={(newValue) => setDueDate(newValue)}
                format="dd.MM.yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: '180px',
                      '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </LocalizationProvider>

        {/* Buyer's Reference (Viittenne) */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {t('Buyers reference')}
          </Typography>
          <TextField
            value={buyersReference}
            onChange={(e) => setBuyersReference(e.target.value)}
            placeholder={t('Buyers reference')}
            size="small"
            inputProps={{ maxLength: 35 }}
            sx={{
              width: '300px',
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
            }}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Customer Email */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {t('Customer email')}
          </Typography>
          <TextField
            type="email"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            placeholder="email@example.com"
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
            }}
          />
        </Box>

        {/* Customer Address */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {t('Customer address')}
          </Typography>
          <TextField
            value={customerAddressStreet}
            onChange={(e) => setCustomerAddressStreet(e.target.value)}
            placeholder={t('Street Address')}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              mb: 1.5,
            }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              value={customerAddressPostalCode}
              onChange={(e) => setCustomerAddressPostalCode(e.target.value)}
              placeholder={t('Postal Code')}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                flex: 1,
              }}
            />
            <TextField
              value={customerAddressCity}
              onChange={(e) => setCustomerAddressCity(e.target.value)}
              placeholder={t('City')}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                flex: 2,
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Notes */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {t('Invoice notes')}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 1 }}>
            {t('Visible to customer on the invoice')}
          </Typography>
          <TextField
            inputRef={notesRef}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('Optional notes')}
            fullWidth
            size="small"
            multiline
            rows={3}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
            }}
          />
        </Box>

        {/* Footer Text */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {t('Footer text')}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 1 }}>
            {t('Shown at the bottom of the invoice')}
          </Typography>
          <TextField
            value={footerText}
            onChange={(e) => setFooterText(e.target.value)}
            placeholder={t('Optional footer')}
            fullWidth
            size="small"
            multiline
            rows={2}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
            }}
          />
        </Box>

        {/* Language */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {t('Invoice language')}
          </Typography>
          <LanguageSelect
            size="small"
            sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          sx={{
            borderRadius: '8px',
            px: 2.5,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          sx={{
            bgcolor: 'secondary.main',
            borderRadius: '8px',
            px: 2.5,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            '&:hover': { bgcolor: 'secondary.dark' },
          }}
        >
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditInvoiceHeaderDialog;
